import React, { useState } from 'react';
import { Loader2, Printer } from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { downloadBillingCycleUb04Pdf } from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import { getUserFacingApiMessageAsync } from '../../utils/apiErrorDetail';
import { usePdfTab } from '../../hooks/usePdfTab';

const CLAIM_ERROR_FALLBACK =
  'Unexpected error occurred. Please try again or contact support';

export interface BillingCycleUb04DownloadButtonProps {
  billingCycleId: string;
  /** When set, render as a full-width labeled menu row instead of an icon-only button. */
  label?: string;
}

export const BillingCycleUb04DownloadButton: React.FC<
  BillingCycleUb04DownloadButtonProps
> = ({ billingCycleId, label }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { error: showError } = useToast();
  const openPdfTab = usePdfTab();

  // Pass the full error/result (not just `.error`) so getUserFacingApiMessageAsync
  // can read the HTTP status and suppress 5xx/proxy bodies centrally.
  const showClaimError = async (error: unknown) => {
    showError(
      'Failed to generate claim',
      await getUserFacingApiMessageAsync(error, CLAIM_ERROR_FALLBACK)
    );
  };

  const handleClick = async () => {
    // Open the tab synchronously inside the click so the browser treats it as
    // user-initiated; otherwise opening it after the awaited fetch below is
    // blocked by popup blockers.
    const tab = openPdfTab();
    if (!tab) return;

    setIsDownloading(true);
    try {
      const resp = await downloadBillingCycleUb04Pdf({
        path: { billing_cycle_id: billingCycleId },
        responseType: 'blob',
      });
      if (resp.error) {
        tab.close();
        console.error('Failed to download UB-04 PDF:', resp.error);
        await showClaimError(resp);
        return;
      }
      if (!resp.data) {
        tab.close();
        console.error('Failed to download UB-04 PDF: no data returned');
        showError('Failed to generate claim', CLAIM_ERROR_FALLBACK);
        return;
      }
      const blob =
        resp.data instanceof Blob
          ? resp.data
          : new Blob([resp.data as BlobPart], { type: 'application/pdf' });
      tab.setBlob(blob);
    } catch (error) {
      tab.close();
      console.error('Failed to download UB-04 PDF:', error);
      await showClaimError(error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (label) {
    return (
      <Button
        type='button'
        variant='ghost'
        onClick={() => void handleClick()}
        disabled={isDownloading}
        className='w-full justify-start gap-2 text-muted-foreground hover:text-foreground'
        aria-label={label}
        title='Open a printable UB-04 claim form (PDF)'
      >
        {isDownloading ? (
          <Loader2 className='h-4 w-4 animate-spin' />
        ) : (
          <Printer className='h-4 w-4' />
        )}
        {label}
      </Button>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            onClick={() => void handleClick()}
            disabled={isDownloading}
            className='text-muted-foreground hover:text-foreground'
            aria-label='Print UB-04 PDF'
          >
            {isDownloading ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <Printer className='h-4 w-4' />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side='bottom' className='text-xs'>
          Print UB-04 PDF
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
